import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import NRControlledTextField from '../NRControlledTextField/NRControlledTextField';

export default function ChipInput({
  control,
  setValue,
  values = [],
  setValues,
  label,
  placeholder,
  error,
  helperText,
  helperErrorText,
  onEnter,
  onRestore,
  name
}) {
  const [editingChip, setEditingChip] = useState(null);

  const inputRef = useRef(null);

  const handleDelete = index => {
    const newValues = values.filter((_, i) => i !== index);
    setValues(newValues);
  };

  const handleChipClick = index => {
    const clickedVal = values[index];

    if (inputRef.current) {
      inputRef.current.focus();
    }

    let newValues = values.filter((_, i) => i !== index);

    if (editingChip) {
      newValues = [...newValues, editingChip];
      if (onRestore) onRestore(editingChip);
    }

    setValues(newValues);

    setValue(name, clickedVal, { shouldDirty: true });
    setValue(`${name}Error`, '');
    setEditingChip(clickedVal);
  };

  const handleAddValue = val => {
    if (!val || !val.trim()) return;

    const trimmed = val.trim();
    const success = onEnter(trimmed, editingChip);

    if (success) {
      setValue(name, '');
      setEditingChip(null);
    }
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setValue(name, '');
      setEditingChip(null);
      e.target.blur();
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <NRControlledTextField
        name={name}
        control={control}
        label={label}
        placeholder={placeholder}
        helperErrorText={helperErrorText}
        error={!!error}
        onEnter={handleAddValue}
        setError={msg => setValue(`${name}Error`, msg)}
        defaultValue=""
        helperText={!error && helperText}
        onKeyDown={handleInputKeyDown}
        ref={inputRef}
      />

      {values.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            maxWidth: '100%',
            '&::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            scrollbarWidth: 'none'
          }}
        >
          {values.map((val, index) => (
            <Chip
              key={`${name}-${index}`}
              label={val}
              size="small"
              color="secondary"
              variant={editingChip === val ? 'filled' : 'outlined'}
              sx={{ flexShrink: 0, cursor: 'pointer', bgcolor: editingChip === val ? 'action.selected' : 'inherit' }}
              onClick={() => handleChipClick(index)}
              onDelete={() => handleDelete(index)}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleChipClick(index);
                }
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  handleDelete(index);
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
